/**
 * HotspotRecommendations
 * --------------------------------------------------------------------------
 * Prescriptive cluster panel powered by DBSCAN-discovered hotspots.
 *
 * Each card answers the question a GACA inspector or Wildlife Hazard
 * Management Programme officer needs at the start of their review:
 *   "Where do we have a problem, how big is it, and what should we DO about it?"
 *
 * Recommendation codes (from backend wildlife_clustering._recommend):
 *   - fleet_management       → red  (Annex 14 §9.4 escalation)
 *   - pyrotechnic_dispersal  → amber (time-of-day deterrents)
 *   - habitat_modification   → orange (vegetation / drainage / lighting)
 *   - monitoring             → slate (keep an eye on it)
 */
import React from 'react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { AlertTriangle, Bird, Clock, MapPin, Target, Loader2 } from 'lucide-react';

const REC_STYLES = {
  fleet_management: {
    badge: 'bg-rose-500/15 border-rose-500/40 text-rose-200',
    icon: <AlertTriangle className="w-4 h-4 text-rose-400" />,
    label: 'Annex 14 §9.4 — ESCALATE',
  },
  pyrotechnic_dispersal: {
    badge: 'bg-amber-500/15 border-amber-500/40 text-amber-200',
    icon: <Clock className="w-4 h-4 text-amber-400" />,
    label: 'Pyrotechnic Dispersal',
  },
  habitat_modification: {
    badge: 'bg-orange-500/15 border-orange-500/40 text-orange-200',
    icon: <Target className="w-4 h-4 text-orange-400" />,
    label: 'Habitat Modification',
  },
  monitoring: {
    badge: 'bg-slate-500/15 border-slate-500/40 text-slate-300',
    icon: <Bird className="w-4 h-4 text-slate-400" />,
    label: 'Monitoring',
  },
};

function Sev({ severity }) {
  const items = [
    { k: 'destroyed', color: 'text-rose-400' },
    { k: 'substantial', color: 'text-orange-400' },
    { k: 'minor', color: 'text-amber-300' },
    { k: 'none', color: 'text-emerald-400' },
  ];
  return (
    <div className="flex items-center gap-3 text-xs tabular-nums">
      {items.map(({ k, color }) => (
        <span key={k} className={`${color} ${severity[k] === 0 ? 'opacity-40' : ''}`}>
          <span className="font-bold">{severity[k] || 0}</span>{' '}
          <span className="text-slate-500 capitalize">{k}</span>
        </span>
      ))}
    </div>
  );
}

export default function HotspotRecommendations({
  clusters = [],
  loading = false,
  inputPoints = 0,
  epsM = 250,
  minSamples = 3,
}) {
  if (loading) {
    return (
      <div
        className="rounded-lg border border-slate-700 bg-slate-900/40 p-6 flex items-center gap-2 text-slate-400 text-sm"
        data-testid="hotspot-recommendations-loading"
      >
        <Loader2 className="w-4 h-4 animate-spin" />
        Running DBSCAN over {inputPoints || '…'} geo-tagged strikes…
      </div>
    );
  }
  if (!clusters.length) {
    return (
      <div
        className="rounded-lg border border-slate-700 bg-slate-900/40 p-8 text-center"
        data-testid="hotspot-recommendations-empty"
      >
        <Bird className="w-6 h-6 text-slate-600 mx-auto mb-2" />
        <div className="text-slate-400 text-sm font-medium">No clusters detected yet</div>
        <div className="text-slate-500 text-xs mt-1">
          DBSCAN found no group of {minSamples}+ strikes within {epsM}m of each other.
          As the dataset grows, recurring hotspots will surface here automatically.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="hotspot-recommendations">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Target className="w-4 h-4 text-violet-400" />
            Mitigation Recommendations
          </h3>
          <p className="text-xs text-slate-500 mt-0.5">
            DBSCAN clustered {inputPoints} strikes into {clusters.length} actionable hotspot{clusters.length === 1 ? '' : 's'} (ε={epsM}m, min={minSamples}). Sorted by severity.
          </p>
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {clusters.map((c) => {
          const style = REC_STYLES[c.recommendation.code] || REC_STYLES.monitoring;
          const window = c.hour_window
            ? `${String(c.hour_window.start_hour).padStart(2, '0')}:00–${String(c.hour_window.end_hour).padStart(2, '0')}:59`
            : null;
          return (
            <Card
              key={c.id}
              className="bg-slate-800/40 border-slate-700"
              data-testid={`hotspot-cluster-${c.rank}`}
            >
              <CardContent className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className="bg-slate-900 border border-slate-700 text-slate-300 text-[10px] uppercase tracking-wider">
                      Cluster #{c.rank}
                    </Badge>
                    <Badge className={`border text-[10px] uppercase tracking-wider gap-1 ${style.badge}`}>
                      {style.icon}
                      {style.label}
                    </Badge>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-white tabular-nums leading-none">{c.count}</div>
                    <div className="text-[10px] text-slate-500 uppercase tracking-wider">strikes</div>
                  </div>
                </div>

                <Sev severity={c.severity} />

                <div className="text-xs space-y-1">
                  {c.dominant_airport && (
                    <div className="flex items-center gap-2 text-slate-300">
                      <MapPin className="w-3 h-3 text-slate-500" />
                      <span className="font-medium">{c.dominant_airport.value}</span>
                      {c.dominant_runway && (
                        <span className="text-slate-400">· R/W {c.dominant_runway.value}</span>
                      )}
                      <span className="text-slate-500 ml-auto">
                        spread {Math.round(c.spread_m)}m
                      </span>
                    </div>
                  )}
                  {window && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Clock className="w-3 h-3 text-slate-500" />
                      <span className="tabular-nums">{window}</span>
                      <span className="text-slate-500 text-[10px]">
                        ({c.hour_window.samples} sample{c.hour_window.samples === 1 ? '' : 's'})
                      </span>
                    </div>
                  )}
                  {c.dominant_species && (
                    <div className="flex items-center gap-2 text-slate-400">
                      <Bird className="w-3 h-3 text-slate-500" />
                      <span>{c.dominant_species.value}</span>
                      {c.dominant_species.count > 1 && (
                        <span className="text-slate-500 text-[10px]">
                          ×{c.dominant_species.count}
                        </span>
                      )}
                    </div>
                  )}
                </div>

                <div className="border-t border-slate-700 pt-3">
                  <div className="text-xs font-bold text-white mb-1">{c.recommendation.title}</div>
                  <div className="text-xs text-slate-400 leading-relaxed">
                    {c.recommendation.detail}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
